import { useState, useEffect } from "react";
import { Menu, X, Home, User, Briefcase, Building2, Layers, Mic2, Mail, Sparkles, BookOpen, ShoppingCart } from "lucide-react";
import { SecnosticIcon } from "@/components/icons/SecnosticIcon";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import fpLogo from "@/assets/fp-logo.png";

const navItems = [
  { label: "Start", href: "#start", Icon: Home },
  { label: "Ihr Partner", href: "#about", Icon: User },
  { label: "Angebot", href: "#services", Icon: Briefcase },
  { label: "Workbook", href: "/workbook", Icon: BookOpen },
  { label: "Primebase", href: "#primebase", Icon: Layers },
  { label: "secnostic", href: "#secnostic", Icon: SecnosticIcon },
  { label: "Speaker", href: "#speaker", Icon: Mic2 },
  { label: "Kontakt", href: "#contact", Icon: Mail },
];

const CollapsibleText = ({ text, visible, className = "" }: { text: string; visible: boolean; className?: string }) => (
  <span className="inline-flex overflow-hidden">
    {text.split("").map((char, i) => (
      <motion.span
        key={i}
        initial={false}
        animate={{
          width: visible ? "auto" : 0,
          opacity: visible ? 1 : 0,
        }}
        transition={{ duration: 0.3, delay: visible ? i * 0.02 : (text.length - i) * 0.02 }}
        className={`inline-block overflow-hidden whitespace-nowrap ${className}`}
      >
        {char}
      </motion.span>
    ))}
  </span>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isWorkbook = location.pathname.startsWith("/workbook");

  const handleCtaClick = () => {
    if (isWorkbook) {
      window.dispatchEvent(new CustomEvent("open-workbook-order"));
    } else {
      window.dispatchEvent(new CustomEvent("open-contact-form"));
    }
  };

  const handleNav = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setOpen(false);
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/" + href);
        setTimeout(() => {
          const target = document.querySelector(href);
          target?.scrollIntoView({ behavior: "smooth" });
        }, 300);
        return;
      }
      const target = document.querySelector(href);
      if (target) {
        setTimeout(() => target.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <nav className="bg-background/80 backdrop-blur-lg border-b border-border/50">
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
        <a href="/" onClick={(e) => handleNav(e, "#start")} className="flex items-center gap-2 font-display font-bold text-lg tracking-tight text-foreground">
          <motion.img
            src={fpLogo}
            alt="FACHSPRECHPARTNER Logo"
            initial={false}
            animate={{
              width: scrolled ? 36 : 0,
              opacity: scrolled ? 1 : 0,
              scale: scrolled ? 1 : 0.6,
            }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="h-9 rounded-lg object-contain"
          />
          <span className="inline-flex items-center">
            <CollapsibleText text="F" visible={!scrolled} />
            <CollapsibleText text="ACHSPRECH" visible={!scrolled} />
            <CollapsibleText text="P" visible={!scrolled} className="text-secondary" />
            <CollapsibleText text="ARTNER" visible={!scrolled} className="text-secondary" />
          </span>
        </a>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map(({ href, label, Icon }) => (
            <a
              key={href}
              href={href}
              onClick={(e) => handleNav(e, href)}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon size={13} strokeWidth={2} />
              {label}
            </a>
          ))}
          <AnimatePresence>
            {scrolled && (
              <motion.button
                key="cta-desktop"
                type="button"
                initial={{ opacity: 0, x: 20, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.9 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                onClick={handleCtaClick}
                className="inline-flex items-center gap-2 bg-cta text-cta-foreground px-5 py-2.5 rounded-md font-bold text-sm uppercase tracking-wider ring-1 ring-primary/20 shadow-md shadow-cta/30 hover:bg-cta-hover hover:shadow-lg hover:shadow-cta/50 hover:-translate-y-0.5 transition-all"
              >
                {isWorkbook ? <ShoppingCart size={15} strokeWidth={2.75} /> : <Sparkles size={15} strokeWidth={2.75} />}
                {isWorkbook ? "Jetzt kaufen" : "Jetzt starten"}
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile right side */}
        <div className="flex items-center gap-2 md:hidden">
          <AnimatePresence>
            {scrolled && (
              <motion.button
                key="cta-mobile"
                type="button"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.25 }}
                onClick={handleCtaClick}
                className="inline-flex items-center gap-1.5 bg-cta text-cta-foreground px-3.5 py-2 rounded-md font-bold text-xs uppercase tracking-wider ring-1 ring-primary/20 shadow-md shadow-cta/30"
              >
                {isWorkbook ? <ShoppingCart size={13} strokeWidth={2.75} /> : <Sparkles size={13} strokeWidth={2.75} />}
                {isWorkbook ? "Jetzt kaufen" : "Jetzt starten"}
              </motion.button>
            )}
          </AnimatePresence>
          <button
            onClick={() => setOpen(!open)}
            className="text-foreground"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {navItems.map(({ href, label, Icon }) => (
                <a
                  key={href}
                  href={href}
                  onClick={(e) => handleNav(e, href)}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  <Icon size={14} strokeWidth={2} />
                  {label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </nav>
    </div>
  );
};

export default Navbar;
